// src/services/pushNotifications.js
// Client-side push notification helpers
// Handles service worker registration, subscription, and Firestore storage
// Supports multi-device: each browser/device gets its own subscription document

import { db, firebaseEnabled } from './firebase';
import { doc } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc, deleteDoc } from './firestoreWrite';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/**
 * Generate a stable device ID for this browser.
 * Uses the push subscription endpoint as the unique key (most reliable),
 * or falls back to a random ID stored in localStorage.
 */
function getDeviceId() {
    let deviceId = localStorage.getItem('mom-push-device-id');
    if (!deviceId) {
        deviceId = Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
        localStorage.setItem('mom-push-device-id', deviceId);
    }
    return deviceId;
}

/**
 * Get the Firestore document ID for this user+device combo
 */
function getSubscriptionDocId(userId) {
    return `${userId}_${getDeviceId()}`;
}

/**
 * Check if push notifications are supported in this browser/environment
 */
export function isPushSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && !!VAPID_PUBLIC_KEY;
}

/**
 * Get the current notification permission state
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getPermissionState() {
    if (!isPushSupported()) return 'unsupported';
    return Notification.permission;
}

/**
 * Register the service worker if not already registered
 */
async function getServiceWorkerRegistration() {
    if (!('serviceWorker' in navigator)) return null;
    try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        return registration;
    } catch (e) {
        console.error('Service worker registration failed:', e);
        return null;
    }
}

/**
 * Convert a base64url string to a Uint8Array (needed for applicationServerKey)
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Subscribe the user to push notifications on this device
 * @param {string} userId - Clerk user ID
 * @returns {boolean} success
 */
export async function subscribeToPush(userId) {
    if (!isPushSupported() || !firebaseEnabled || !userId) return false;

    try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return false;

        const registration = await getServiceWorkerRegistration();
        if (!registration) return false;

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });

        // Store keyed by userId + deviceId so each device has its own document
        const subJson = subscription.toJSON();
        const docId = getSubscriptionDocId(userId);
        await setDoc(doc(db, 'pushSubscriptions', docId), {
            userId,
            subscription: subJson,
            createdAt: new Date().toISOString(),
            userAgent: navigator.userAgent || '',
        });

        return true;
    } catch (e) {
        console.error('Push subscription failed:', e);
        return false;
    }
}

/**
 * Unsubscribe from push notifications on this device
 * @param {string} userId - Clerk user ID
 * @returns {boolean} success
 */
export async function unsubscribeFromPush(userId) {
    if (!firebaseEnabled || !userId) return false;

    try {
        const registration = await getServiceWorkerRegistration();
        if (registration) {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
            }
        }

        // Remove this device's subscription from Firestore
        const docId = getSubscriptionDocId(userId);
        await deleteDoc(doc(db, 'pushSubscriptions', docId));
        return true;
    } catch (e) {
        console.error('Push unsubscribe failed:', e);
        return false;
    }
}

/**
 * Check if the current device is subscribed to push
 * @returns {boolean}
 */
export async function isSubscribedToPush() {
    if (!isPushSupported()) return false;
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        return !!subscription;
    } catch (e) {
        return false;
    }
}